import axios from "../utils/axios";
import { decryptData, encryptData, generateKey } from "../utils/index.jsx";
import {
    deriveInvitationSecrets,
    generateRootToken,
    mintWrapSalt,
    unwrapInvestigationKey,
    wrapInvestigationKey,
} from "../utils/invitationWrap";
import { AcceptFragment, buildAcceptLink } from "../utils/invitationLink";

/**
 * SHER-803 - both halves of the per-invitation key-handoff (SHER-769 decision doc, 3.3 and 3.6).
 *
 * Inviter: mint `S` and a salt per invitee, wrap the investigation key under `wrapKey`, send the
 * API the blob, `authProof` and the salt, and build the accept link from the `invitationUuid` the
 * API answers with. Invitee: read the link, derive `authProof` and `wrapKey`, ask for the link's
 * state, register (if new) and accept by re-wrapping the key under their own personal key.
 *
 * WHAT NEVER LEAVES THIS MODULE: `S` and the raw investigation key. Every request carries
 * `authProof`, a salt, an opaque `siw1:` blob or a CryptoJS personal wrap - the same things the
 * API already stores. `invitations.test.ts` captures every request and console line this module
 * produces and searches them for both secrets.
 *
 * Nothing here logs. On failure it returns a reason instead, so no caller is tempted to print an
 * object that holds a fragment.
 */

const API = () => import.meta.env.VITE_APP_API_URL;
const authHeaders = () => ({ headers: { Authorization: localStorage.getItem("jwt") } });

/** `share_status` value the API uses for an accepted invitation. */
const ACCEPTED = 2;

export interface InviteeRequest {
    email: string;
    roleId: number;
}

export interface TokenInviteePayload extends InviteeRequest {
    keyResearcherInvestigation: string;
    authProof: string;
    wrapSalt: string;
}

/**
 * One invitee's share entry plus the secret that must stay behind. `rootToken` is returned to the
 * caller only so it can be put in that invitee's link; it is not part of `payload`.
 *
 * `investigationOnEmptyKey` is the SHER-697 carve-out: an investigation whose stored key is itself
 * the empty wrap may onboard staff into that same state. Anywhere else an empty key means the
 * inviter's session lost its key, and wrapping it would hand every invitee nothing (SHER-344).
 */
export async function prepareTokenInvitee(
    invitee: InviteeRequest,
    rawKeyInvestigation: string,
    options: { investigationOnEmptyKey?: boolean } = {},
): Promise<{ payload: TokenInviteePayload; rootToken: string }> {
    if (!rawKeyInvestigation && !options.investigationOnEmptyKey) {
        throw new Error("SHER-803: refusing to invite with an empty investigation key - the inviter's key is unavailable; sign in again.");
    }
    const rootToken = generateRootToken();
    const wrapSalt = mintWrapSalt();
    const { wrapKey, authProof } = await deriveInvitationSecrets(rootToken, wrapSalt);
    const keyResearcherInvestigation = await wrapInvestigationKey(wrapKey, rawKeyInvestigation || "");
    return {
        payload: { email: invitee.email, roleId: invitee.roleId, keyResearcherInvestigation, authProof, wrapSalt },
        rootToken,
    };
}

export interface InvitationLink {
    email: string;
    link: string;
    expiresAt: string;
}

export interface ShareWithTokensResult {
    status: number;
    errorCode?: number;
    errorMessage?: string;
    sharedResearchers?: unknown[];
    /** One per invitee, for the inviter to deliver. Present only on success. */
    links?: InvitationLink[];
    /** Invitees the API enrolled but for whom no link could be built - they need a Resend. */
    missingLinks?: string[];
}

/**
 * Invite, and build each invitee's accept link. The links exist only in the returned value: the
 * API cannot rebuild them (it never had `S`), so a caller that drops them has to re-invite.
 */
export async function shareInvestigationWithTokens(
    investigationUuid: string,
    invitees: InviteeRequest[],
    rawKeyInvestigation: string,
    origin: string,
    options: { investigationOnEmptyKey?: boolean } = {},
): Promise<ShareWithTokensResult> {
    const prepared = await Promise.all(invitees.map((invitee) => prepareTokenInvitee(invitee, rawKeyInvestigation, options)));
    const response = await axios.post(
        `${API()}/researcher/investigation/${investigationUuid}/share`,
        { listResearchers: prepared.map((p) => p.payload) },
        authHeaders(),
    );
    const data = response.data || {};
    if (response.status !== 200) {
        return { status: response.status, errorCode: data.errorCode, errorMessage: data.errorMessage };
    }

    // The API issues invitations in request order; pair by position, and confirm by email so a
    // mismatch can never put one person's secret in another person's link. The invitations are
    // already committed at this point, so nothing here may throw: an unpaired invitee is reported
    // (they need a Resend), never allowed to cost everyone else their link.
    const issuedList: { email: string; invitationUuid: string; expiresAt: string }[] = data.invitations || [];
    const links: InvitationLink[] = [];
    const missingLinks: string[] = [];
    prepared.forEach((mine, index) => {
        const issued = issuedList[index];
        if (issued && issued.email.trim().toLowerCase() === mine.payload.email.trim().toLowerCase()) {
            links.push({
                email: issued.email,
                link: buildAcceptLink(origin, issued.invitationUuid, mine.rootToken, mine.payload.wrapSalt),
                expiresAt: issued.expiresAt,
            });
        } else {
            missingLinks.push(mine.payload.email);
        }
    });
    return { status: 200, sharedResearchers: data.sharedResearchers, links, missingLinks };
}

export type InvitationLinkState = "invalid" | "valid" | "expired" | "used" | "error";

export interface InvitationStatus {
    state: InvitationLinkState;
    email?: string;
    investigationName?: string;
    investigationUuid?: string;
    inviterName?: string;
    expiresAt?: string;
    needsAccount?: boolean;
}

async function proofOf(fragment: AcceptFragment): Promise<string> {
    return (await deriveInvitationSecrets(fragment.rootToken, fragment.wrapSalt)).authProof;
}

/**
 * Where this link stands. `error` means the API could not be asked (offline, 5xx) - it must not be
 * shown as a broken link, because the link may be fine.
 */
export async function fetchInvitationStatus(fragment: AcceptFragment): Promise<InvitationStatus> {
    try {
        const response = await axios.post(`${API()}/researcher/invitation/status`, {
            invitationId: fragment.invitationId,
            authProof: await proofOf(fragment),
        });
        if (response.status !== 200 || !response.data || !response.data.invitation) {
            return { state: "error" };
        }
        return response.data.invitation;
    } catch (error) {
        return { state: "error" };
    }
}

/** The fields an invited registration adds to the usual `/researcher/register` body (SHER-800). */
export async function withInvitationProof<T extends object>(registerPayload: T, fragment: AcceptFragment): Promise<T & { invitationId: string; authProof: string }> {
    return { ...registerPayload, invitationId: fragment.invitationId, authProof: await proofOf(fragment) };
}

export type AcceptResult =
    | { ok: true }
    | { ok: false; reason: "invalid" | "expired" | "used" | "no_personal_key" | "error" };

/**
 * Accept: open the investigation key with the link, re-wrap it under the invitee's own key, and
 * send the re-wrap with the proof. The API redeems the token in the same transaction.
 *
 * Every local failure returns BEFORE a request: a link that does not open the blob (AES-GCM
 * throws, it never yields ""), a missing personal key, or a re-wrap that does not round-trip.
 * Storing any of those would be the SHER-344 failure again - a membership whose key opens nothing.
 */
export async function acceptTokenInvitation(params: {
    fragment: AcceptFragment;
    investigationUuid: string;
    wrappedKey: string;
    rawKeyResearcher: string | null;
}): Promise<AcceptResult> {
    const { fragment, investigationUuid, wrappedKey, rawKeyResearcher } = params;
    if (!rawKeyResearcher) {
        return { ok: false, reason: "no_personal_key" };
    }

    let investigationKey: string;
    let authProof: string;
    try {
        const secrets = await deriveInvitationSecrets(fragment.rootToken, fragment.wrapSalt);
        investigationKey = await unwrapInvestigationKey(secrets.wrapKey, wrappedKey);
        authProof = secrets.authProof;
    } catch (error) {
        return { ok: false, reason: "invalid" };
    }

    const personalWrap = encryptData(investigationKey, rawKeyResearcher);
    if (decryptData(personalWrap, rawKeyResearcher) !== investigationKey) {
        return { ok: false, reason: "error" };
    }

    try {
        const response = await axios.put(
            `${API()}/researcher/investigation/${investigationUuid}/answer`,
            { answer: ACCEPTED, keyInvestigationResearcher: personalWrap, invitationId: fragment.invitationId, authProof },
            authHeaders(),
        );
        if (response.status === 200) {
            return { ok: true };
        }
        const code = response.data && response.data.errorCode;
        return { ok: false, reason: code === 10 ? "invalid" : code === 11 ? "expired" : code === 5 ? "used" : "error" };
    } catch (error) {
        return { ok: false, reason: "error" };
    }
}

/**
 * Ask the inviter for a new link (SHER-804). The invitee cannot re-issue one themselves - a new
 * link means wrapping the investigation key again, and only a member holds it.
 */
export async function requestInvitationResend(fragment: AcceptFragment): Promise<boolean> {
    try {
        const response = await axios.post(`${API()}/researcher/invitation/resend-request`, {
            invitationId: fragment.invitationId,
            authProof: await proofOf(fragment),
        });
        return response.status === 200;
    } catch (error) {
        return false;
    }
}

export interface ResendResult {
    status: number;
    errorCode?: number;
    /** The new link to hand over. Present only on success. */
    link?: InvitationLink;
}

/**
 * Re-issue a still-pending invitation (SHER-804): a fresh `S` and wrap, exactly as at share, sent
 * to the resend endpoint. The API expires the older links, re-sends the email and answers with the
 * new `invitationUuid`; the new link exists only in the returned value.
 */
export async function resendInvitationWithToken(
    investigationUuid: string,
    email: string,
    rawKeyInvestigation: string,
    origin: string,
    options: { investigationOnEmptyKey?: boolean } = {},
): Promise<ResendResult> {
    const { payload, rootToken } = await prepareTokenInvitee({ email, roleId: 0 }, rawKeyInvestigation, options);
    const { roleId, ...body } = payload;
    void roleId;
    const response = await axios.post(`${API()}/researcher/investigation/${investigationUuid}/invitation/resend`, body, authHeaders());
    const data = response.data || {};
    if (response.status !== 200 || !data.invitation) {
        return { status: response.status, errorCode: data.errorCode };
    }
    return {
        status: 200,
        link: {
            email: data.invitation.email,
            link: buildAcceptLink(origin, data.invitation.invitationUuid, rootToken, payload.wrapSalt),
            expiresAt: data.invitation.expiresAt,
        },
    };
}

/**
 * SHA-256, base64 - the exact value the regular sign-up and sign-in send for a password
 * (`CryptoJS.SHA256(p).toString(CryptoJS.enc.Base64)` in register.jsx / authService.js). WebCrypto
 * here only because crypto-js ships no types; the test pins the two as equal.
 */
async function sha256Base64(value: string): Promise<string> {
    const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(value) as unknown as BufferSource);
    return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(digest))));
}

export interface InvitedAccount {
    email: string;
    name: string;
    surnames: string;
    password: string;
    language: string;
    cloudflareToken: string;
}

/**
 * Create the invitee's account (SHER-800's invited branch). Same key chain as the regular sign-up
 * in components/register.jsx: a fresh personal key wrapped under the password, and only the
 * password's SHA-256 sent. The invitation proof is what authorises claiming the placeholder.
 */
export async function registerInvitedResearcher(account: InvitedAccount, fragment: AcceptFragment): Promise<{ ok: boolean; status: number }> {
    const researcherKey = await generateKey();
    const payload = await withInvitationProof({
        email: account.email,
        name: account.name,
        surnames: account.surnames,
        language: account.language,
        cloudflareToken: account.cloudflareToken,
        keyEncrypted: encryptData(researcherKey, account.password),
        password: await sha256Base64(account.password),
    }, fragment);
    try {
        const response = await axios.post(`${API()}/researcher/register`, payload);
        return { ok: response.status === 200, status: response.status };
    } catch (error) {
        return { ok: false, status: 0 };
    }
}
